import { useEffect, useState } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Archive, ArchiveRestore, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiClient, API } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { CASE_STATUS_LABELS, CASE_STATUS_KEYS, PROFILE_LABELS, formatDate, formatDateTime } from "@/constants/business_status";

function Section({ title, children, testId }) {
  return (
    <div data-testid={testId} className="border border-gray-100 rounded-2xl bg-white p-5">
      <h3 className="font-heading font-semibold text-navy text-sm mb-3">{title}</h3>
      <div className="space-y-2 text-sm">{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3">
      <div className="text-navy/70 font-medium text-xs sm:min-w-[160px]">{label}</div>
      <div className="text-navy/90 break-words">{children ?? <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

function Address({ addr }) {
  if (!addr) return <span className="text-muted-foreground">—</span>;
  const line = [addr.address_line_1, addr.postal_code, addr.city].filter(Boolean).join(" · ");
  return (
    <div>
      {line || <span className="text-muted-foreground">Adresse non précisée</span>}
      {addr.floor || addr.elevator ? (
        <div className="text-xs text-muted-foreground mt-1">
          Étage : {addr.floor || "—"} • Ascenseur : {addr.elevator || "—"}
        </div>
      ) : null}
      {addr.access_notes && <div className="text-xs text-muted-foreground mt-1">Accès : {addr.access_notes}</div>}
      {addr.parking_notes && <div className="text-xs text-muted-foreground mt-1">Stationnement : {addr.parking_notes}</div>}
      {addr.raw_input && !line && <div className="text-xs italic text-muted-foreground">{addr.raw_input}</div>}
    </div>
  );
}

export default function CaseDetailPage() {
  const { admin } = useAdminAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [price, setPrice] = useState("");
  const [statusDraft, setStatusDraft] = useState("");

  useEffect(() => {
    if (!admin) return;
    (async () => {
      setLoading(true);
      try {
        const r = await apiClient.get(`/admin/cases/${id}`);
        setData(r.data);
        setNotes(r.data.case.internal_notes || "");
        setPrice(r.data.case.proposed_price || "");
        setStatusDraft(r.data.case.status);
      } catch {
        toast.error("Dossier introuvable");
        navigate("/admin");
      } finally { setLoading(false); }
    })();
  }, [admin, id, navigate]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/admin/cases/${id}`, { internal_notes: notes, proposed_price: price, status: statusDraft });
      toast.success("Dossier mis à jour");
      const r = await apiClient.get(`/admin/cases/${id}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const doArchive = async () => {
    if (!window.confirm("Archiver ce dossier ? Il sera masqué de la liste.")) return;
    await apiClient.post(`/admin/cases/${id}/archive`);
    toast.success("Dossier archivé");
    const r = await apiClient.get(`/admin/cases/${id}`); setData(r.data);
  };
  const doRestore = async () => {
    await apiClient.post(`/admin/cases/${id}/restore`);
    toast.success("Dossier restauré");
    const r = await apiClient.get(`/admin/cases/${id}`); setData(r.data);
  };

  if (loading || !data) return <AdminShell><div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div></AdminShell>;
  const { case: c, contact, organization, lead } = data;

  return (
    <AdminShell>
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button data-testid="case-back" variant="ghost" onClick={() => navigate("/admin")} className="text-navy font-semibold -ml-3"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
          <h1 data-testid="case-detail-header" className="font-heading font-extrabold text-2xl text-navy">
            {c.case_number} — {contact?.display_name || "Contact inconnu"}
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {PROFILE_LABELS[c.profile_type]} • Créé le {formatDateTime(c.created_at)} • {CASE_STATUS_LABELS[c.status]}
            {c.archived_at ? " • Archivé" : ""}
          </p>
        </div>
        <div className="flex gap-2">
          {c.archived_at ? (
            <Button data-testid="case-restore" onClick={doRestore} variant="outline" className="rounded-full"><ArchiveRestore className="w-4 h-4 mr-1" /> Restaurer</Button>
          ) : (
            <Button data-testid="case-archive" onClick={doArchive} variant="outline" className="rounded-full text-orange border-orange/40 hover:bg-orange/5"><Archive className="w-4 h-4 mr-1" /> Archiver</Button>
          )}
        </div>
      </div>

      <div className="mb-5 flex flex-wrap gap-2" data-testid="case-actions-bar">
        <Button data-testid="case-create-quote" onClick={async () => {
          try {
            const existing = await apiClient.get("/admin/quotes", { params: { case_id: id, page_size: 1 } });
            if (existing.data.items.length > 0) return navigate(`/admin/devis/${existing.data.items[0].id}`);
            const r = await apiClient.post("/admin/quotes", { case_id: id });
            toast.success(`Devis ${r.data.quote_number} créé`);
            navigate(`/admin/devis/${r.data.id}`);
          } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        }} className="bg-orange hover:bg-orange/90 text-white rounded-full font-semibold">Devis</Button>
        <Button data-testid="case-open-inventory" onClick={() => navigate(`/admin/dossiers/${id}/inventaire`)} variant="outline" className="rounded-full">Inventaire &amp; accès</Button>
        <Button data-testid="case-portal-link" onClick={async () => {
          try {
            const r = await apiClient.post(`/admin/cases/${id}/portal-link`);
            await navigator.clipboard.writeText(r.data.url).catch(() => {});
            window.prompt("Lien espace client (copié dans le presse-papiers) :", r.data.url);
          } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        }} variant="outline" className="rounded-full">Lien espace client</Button>
        <Button data-testid="case-configure-payment" onClick={async () => {
          try {
            await apiClient.put(`/admin/cases/${id}/payment-plan`, { status: "to_pay" });
            toast.success("Plan de paiement initialisé — gérez-le depuis Paiements");
            navigate("/admin/paiements");
          } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Permission requise"); }
        }} variant="outline" className="rounded-full">Modalités de paiement</Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Résumé" testId="section-summary">
          <Row label="Numéro">{c.case_number}</Row>
          <Row label="Profil">{PROFILE_LABELS[c.profile_type]}</Row>
          <Row label="Prestation">{c.service_type}</Row>
          <Row label="Date souhaitée">{c.requested_date}</Row>
          <Row label="Statut">{CASE_STATUS_LABELS[c.status]}</Row>
          <Row label="Contact préféré">{c.preferred_contact_method}</Row>
        </Section>

        <Section title="Contact" testId="section-contact">
          {contact ? (
            <>
              <Row label="Nom">{contact.display_name}</Row>
              <Row label="Téléphone">{contact.phone}</Row>
              <Row label="Email">{contact.email}</Row>
              <Row label="Type">{contact.contact_type}</Row>
              <Button data-testid="case-contact-open" size="sm" variant="ghost" onClick={() => navigate(`/admin/contacts/${contact.id}`)} className="text-navy font-semibold">Voir la fiche contact →</Button>
            </>
          ) : <span className="text-muted-foreground">Aucun contact rattaché</span>}
        </Section>

        <Section title="Organisation" testId="section-organization">
          {organization ? (
            <>
              <Row label="Nom">{organization.legal_name}</Row>
              <Row label="Type">{organization.organization_type}</Row>
              <Row label="Email">{organization.email}</Row>
              <Row label="Téléphone">{organization.phone}</Row>
              <Button data-testid="case-org-open" size="sm" variant="ghost" onClick={() => navigate(`/admin/organisations/${organization.id}`)} className="text-navy font-semibold">Voir la fiche organisation →</Button>
            </>
          ) : <span className="text-muted-foreground">Dossier particulier &mdash; pas d&apos;organisation</span>}
        </Section>

        <Section title="Trajet" testId="section-route">
          <Row label="Départ"><Address addr={c.origin_address} /></Row>
          <Row label="Arrivée"><Address addr={c.destination_address} /></Row>
        </Section>

        {c.customer_message && (
          <Section title="Demande initiale" testId="section-message">
            <p className="whitespace-pre-wrap text-navy/80">{c.customer_message}</p>
          </Section>
        )}

        {c.files?.length > 0 && (
          <Section title="Pièces jointes" testId="section-files">
            <div className="flex flex-wrap gap-2">
              {c.files.map((f, i) => (
                <a key={f} href={`${API}/files/${f}`} target="_blank" rel="noopener noreferrer" data-testid={`case-file-${i}`} className="text-xs text-orange font-semibold hover:underline bg-orange/10 rounded-full px-3 py-1">
                  Fichier {i + 1}
                </a>
              ))}
            </div>
          </Section>
        )}

        <Section title="Suivi interne" testId="section-suivi">
          <Row label="Statut">
            <Select value={statusDraft} onValueChange={setStatusDraft}>
              <SelectTrigger data-testid="case-status-select" className="rounded-xl h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CASE_STATUS_KEYS.map((s) => <SelectItem key={s} value={s}>{CASE_STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </Row>
          <Row label="Prix proposé">
            <Input data-testid="case-price-input" value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-xl h-9" />
          </Row>
          <Row label="Notes">
            <Textarea data-testid="case-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-xl" />
          </Row>
          <Button data-testid="case-save" onClick={save} disabled={saving} className="bg-navy hover:bg-navy/90 text-white rounded-full font-semibold w-full mt-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
          </Button>
        </Section>

        <Section title="Historique" testId="section-history">
          {c.history && c.history.length > 0 ? (
            <ul className="space-y-2">
              {c.history.slice().reverse().map((h, i) => (
                <li key={i} className="text-xs">
                  <span className="font-semibold text-navy">{h.action}</span>
                  <span className="text-muted-foreground"> • {formatDateTime(h.date)}</span>
                  {h.admin_email && <span className="text-muted-foreground"> • {h.admin_email}</span>}
                  {(h.old_value || h.new_value) && (
                    <div className="text-navy/70 pl-2">
                      {h.old_value && <span>{JSON.stringify(h.old_value)}</span>}
                      {h.old_value && h.new_value && <span> → </span>}
                      {h.new_value && <span>{JSON.stringify(h.new_value)}</span>}
                    </div>
                  )}
                  {h.comment && <div className="text-muted-foreground pl-2">{h.comment}</div>}
                </li>
              ))}
            </ul>
          ) : <span className="text-muted-foreground">Aucun historique</span>}
        </Section>

        {lead && (
          <Section title="Lead d'origine" testId="section-lead">
            <Row label="ID lead">{lead._id}</Row>
            <Row label="Date">{formatDate(lead.created_at)}</Row>
            <Row label="Statut brut">{lead.status}</Row>
            <Row label="Source">{lead.source}</Row>
          </Section>
        )}
      </div>
    </AdminShell>
  );
}
